import { createFileRoute, redirect, useNavigate } from '@tanstack/react-router';
import { z } from 'zod';
import { PageContainer } from '@/components/shared/PageContainer';
import type { TabEntry } from '@/components/shared/PageTabs';
import PageTabs from '@/components/shared/PageTabs';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { useFeatures } from '@/hooks/useFeatures';
import { ensurePermissions, usePermissions } from '@/hooks/usePermissions';
import { useTranslator } from '@/hooks/useTranslator';
import { PLATFORM_FEATURES } from '@/lib/constants/features';
import { AutomationRulesTable } from '../-components/AutomationRulesTable';
import { BlockedLinesSection } from '../-components/BlockedLinesSection';
import { RuleSheet } from '../-components/RuleSheet';
import { RunHistoryDrawer } from '../-components/RunHistoryDrawer';
import {
  type RuleListTab,
  useAutomationRulesList,
} from '../-hooks/useAutomationRulesList';
import { useBlockedLinesCount } from '../-hooks/useBlockedLines';
import { useRuleActions } from '../-hooks/useRuleActions';
import { useRuleEditing } from '../-hooks/useRuleEditing';

/**
 * Tab, table filters and the drawer all live in the URL so a rule that needs
 * attention can be linked to directly — "the blocked ones" is the state people
 * share.
 */
const searchSchema = z.object({
  /**
   * Which surface of the Automation Center. Named `section` because `tab` is
   * already the rules list's active/archive toggle.
   */
  section: z.enum(['rules', 'blocked']).optional(),
  tab: z.enum(['active', 'archive']).optional(),
  create: z.boolean().optional(),
  /** Id of the rule being edited. */
  edit: z.string().optional(),
  /** Id of the rule open read-only. */
  view: z.string().optional(),
  /** Id of the rule whose run history is open. */
  runs: z.string().optional(),
  page: z.number().int().min(1).optional(),
  limit: z.number().int().min(5).max(500).optional(),
  sort: z.string().optional(),
  filters: z.record(z.string(), z.array(z.string())).optional(),
});

export const Route = createFileRoute('/(client)/automation-rules/(pages)/')({
  validateSearch: searchSchema,
  beforeLoad: async () => {
    const perms = await ensurePermissions();
    if (perms['automation-rules'].canRead) return;
    throw redirect({ to: '/dashboard' });
  },
  component: AutomationRulesPage,
});

function AutomationRulesPage() {
  const { isFeatureEnabled } = useFeatures();
  const { 'automation-rules': can } = usePermissions();
  const { getTranslation: t } = useTranslator({ page: 'automationRules' });
  const { getTranslation: tShared } = useTranslator({ page: 'shared' });
  const navigate = useNavigate({ from: Route.fullPath });
  const search = Route.useSearch();

  const tab: RuleListTab = search.tab ?? 'active';

  const { rules, blockedCount, quota, total, isLoading, table } =
    useAutomationRulesList(tab);
  const actions = useRuleActions();
  // Both counts are fetched regardless of which tab is showing — a count that
  // only appeared once you opened the tab would be useless for deciding to.
  const blockedCountTotal = useBlockedLinesCount();

  function setSearch(patch: Partial<z.infer<typeof searchSchema>>) {
    void navigate({ search: current => ({ ...current, ...patch }) });
  }

  /**
   * The blocked queue.
   *
   * `runs`/`view` are cleared in the same navigation — the drawer or sheet the
   * reader came from must not stay open over a different surface.
   */
  function openBlockedSection() {
    setSearch({
      section: 'blocked',
      runs: undefined,
      view: undefined,
      page: 1,
    });
  }

  const editing = useRuleEditing({
    // Switching to edit drops `view` in the same navigation, so the sheet stays
    // mounted and only its body changes.
    onOpen: id => setSearch({ edit: id, view: undefined }),
    onPause: actions.pause,
  });

  if (!isFeatureEnabled(PLATFORM_FEATURES.AUTOMATION_RULES)) {
    return null;
  }

  /**
   * One sheet serves all three: reading a rule, editing it, and creating one.
   *
   * Which mode is showing is whichever param is set, so a mode switch is a
   * search-param change on a sheet that never unmounts — no close-and-reopen,
   * and the reader is not thrown back to the list between the two.
   */
  // Both arrive as search params, so a link is enough to reach them. A reader
  // following one lands on the read-only sheet rather than an editor whose
  // every save would 403.
  const isCreating = search.create === true && can.canCreate;
  const isEditing = isCreating || (Boolean(search.edit) && can.canEdit);
  const sheetOpen = isEditing || Boolean(search.view) || Boolean(search.edit);
  const sheetRuleId =
    (isEditing ? search.edit : (search.view ?? search.edit)) ?? null;

  const section = search.section ?? 'rules';
  const tabs: TabEntry[] = [
    { key: 'rules', label: t('tabs.rules'), count: total },
    { key: 'blocked', label: t('tabs.blocked'), count: blockedCountTotal },
  ];

  return (
    <>
      <PageTabs
        tabs={tabs}
        param='section'
        defaultTab='rules'
        moreLabel={tShared('moreTabs')}
      />
      <PageContainer>
        {section === 'blocked' ? (
          <BlockedLinesSection
            // Opening a rule from the blocked view lands on its read-only
            // sheet, with the rules surface restored behind it.
            onOpenRule={ruleId => setSearch({ section: 'rules', view: ruleId })}
          />
        ) : (
          <AutomationRulesTable
            rules={rules}
            total={total}
            blockedCount={blockedCount}
            quota={quota}
            isLoading={isLoading}
            tab={tab}
            // Switching tab resets paging — page 4 of the active list means
            // nothing in the archive.
            onTabChange={next => setSearch({ tab: next, page: 1 })}
            table={table}
            busyRuleId={actions.busyRuleId}
            onPause={actions.pause}
            onResume={actions.resume}
            onArchive={actions.archive}
            onRestore={actions.restore}
            onEdit={editing.requestEdit}
            onRun={actions.run}
            can={can}
            onViewRuns={rule => setSearch({ runs: rule.id })}
            onViewBlocked={() => openBlockedSection()}
            onViewRule={rule => setSearch({ view: rule.id })}
            onCreate={() => setSearch({ create: true })}
          />
        )}

        {/* Mounted for both sections: a `?view=` link has to open the sheet
            wherever the reader arrived from, including the blocked view's
            "Open rule". */}
        <RuleSheet
          open={sheetOpen}
          mode={isEditing ? 'edit' : 'view'}
          ruleId={sheetRuleId}
          quota={quota}
          // Dismissing clears every mode at once — whichever one was showing.
          onOpenChange={open => {
            if (!open) {
              setSearch({
                view: undefined,
                edit: undefined,
                create: undefined,
              });
            }
          }}
          // Saved or cancelled. An edited rule has a view to return to, and that
          // is where the author wants to land — the change is worth re-reading.
          // A created one does not exist under a known id yet, so the sheet closes
          // and the new row is waiting in the list behind it.
          onEditDone={() =>
            setSearch(
              isCreating
                ? { create: undefined }
                : { edit: undefined, view: search.edit },
            )
          }
          // Read-only needs no pause to open; going to edit still does, and the
          // prompt below is that gate.
          onRequestEdit={editing.requestEdit}
          busyRuleId={actions.busyRuleId}
          onRun={actions.run}
          onPause={actions.pause}
          onResume={actions.resume}
          onArchive={actions.archive}
          onRestore={actions.restore}
        />

        <RunHistoryDrawer
          open={Boolean(search.runs)}
          ruleId={search.runs ?? null}
          // Taken from the loaded page so the header names the rule without a
          // second fetch; null falls back to a generic subtitle.
          ruleName={rules.find(rule => rule.id === search.runs)?.name ?? null}
          onOpenChange={open =>
            setSearch({ runs: open ? search.runs : undefined })
          }
          onViewBlocked={() => {
            if (search.runs) openBlockedSection();
          }}
        />

        {/* An active rule has to be paused before its definition can change —
          the API refuses otherwise. Asking here beats letting the author fill
          in the form and meet the refusal at the save button. */}
        <AlertDialog
          open={Boolean(editing.pendingRule)}
          // Blocks dismissal while the pause is in flight — the drawer must not
          // open until it has landed.
          loading={editing.isPausing}
          onOpenChange={open => {
            if (!open) editing.cancelPause();
          }}
        >
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>{t('list.pauseToEdit.title')}</AlertDialogTitle>
              <AlertDialogDescription>
                {t('list.pauseToEdit.description')}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel disabled={editing.isPausing}>
                {t('list.pauseToEdit.cancel')}
              </AlertDialogCancel>
              <AlertDialogAction
                onClick={event => {
                  // The dialog would close on its own before the pause lands,
                  // and the drawer must not open until it has.
                  event.preventDefault();
                  void editing.confirmPauseAndEdit();
                }}
                disabled={editing.isPausing}
              >
                {t('list.pauseToEdit.confirm')}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </PageContainer>
    </>
  );
}
